export type SignalType = 'positive' | 'negative' | 'neutral'

export interface Signal {
  id: string
  text: string
  type: SignalType
  source: string
  detail: string
}

export interface ReviewBreakdown {
  overall: number
  reviewCount: number
  careerGrowth: number
  compensationBenefits: number
  management: number
  workLifeBalance: number
  companyCulture: number
}

export interface Company {
  id: string
  name: string
  logoUrl: string | null
  industry: string
  size: string
  stage: string
  funding: string
  matchScore: number
  signals: Signal[]
  reviewSnippet: string
  reviewRating: number
  blindBreakdown: ReviewBreakdown | null
  glassdoorRating: number | null
  glassdoorBreakdown: ReviewBreakdown | null
  lastUpdated: string
}

export const mockCompanies: Company[] = [
  {
    id: '1',
    name: 'Cohere',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/3pcUA2YmiWNR7omZaAoRVZ/cohere.png',
    industry: 'AI / NLP',
    size: '500-1k',
    stage: 'Series D',
    funding: '$445M',
    matchScore: 91,
    signals: [
      {
        id: 's1',
        text: '16wk parental leave',
        type: 'positive',
        source: 'Benefits page, 2025',
        detail:
          'Offers 16 weeks fully paid parental leave for all parents regardless of gender.',
      },
      {
        id: 's2',
        text: 'Trans healthcare',
        type: 'positive',
        source: 'HRC Corporate Equality Index 2025',
        detail:
          'Covers gender-affirming surgeries, hormone therapy, and mental health support.',
      },
      {
        id: 's3',
        text: 'No layoffs 5yr',
        type: 'positive',
        source: 'Layoffs.fyi, WARN filings',
        detail:
          'No reported layoffs or significant workforce reductions since founding.',
      },
      {
        id: 's4',
        text: 'Published DEI report',
        type: 'positive',
        source: 'Company website',
        detail:
          'Publishes annual DEI report with detailed metrics and accountability measures.',
      },
    ],
    reviewSnippet:
      'Great culture with a genuine focus on responsible AI. Leadership is transparent and the benefits are top-tier for a startup.',
    reviewRating: 4.6,
    blindBreakdown: {
      overall: 4.6,
      reviewCount: 39,
      careerGrowth: 4.7,
      compensationBenefits: 4.9,
      management: 4.5,
      workLifeBalance: 4.3,
      companyCulture: 4.6,
    },
    glassdoorRating: 4.4,
    glassdoorBreakdown: {
      overall: 4.4,
      reviewCount: 52,
      careerGrowth: 4.5,
      compensationBenefits: 4.6,
      management: 4.2,
      workLifeBalance: 4.1,
      companyCulture: 4.4,
    },
    lastUpdated: 'Mar 15, 2026',
  },
  {
    id: '2',
    name: 'DeepMind',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/nC4zYAs9bwwQynWXVZyzdh/deepmind.png',
    industry: 'AI Research',
    size: '1k-5k',
    stage: 'Subsidiary',
    funding: 'Alphabet (GOOG)',
    matchScore: 78,
    signals: [
      {
        id: 's5',
        text: 'Diverse leadership (38%)',
        type: 'positive',
        source: 'Annual DEI report 2025',
        detail:
          '38% of leadership positions held by underrepresented minorities.',
      },
      {
        id: 's6',
        text: 'Flexible hours',
        type: 'positive',
        source: 'Employee handbook, Glassdoor reviews',
        detail:
          'Flexible working hours with core collaboration windows. Strong async culture.',
      },
      {
        id: 's7',
        text: 'Recent layoffs',
        type: 'negative',
        source: 'Layoffs.fyi, Jan 2026',
        detail:
          'Parent company Alphabet conducted layoffs affecting some DeepMind research teams.',
      },
      {
        id: 's8',
        text: 'No sentiment data',
        type: 'neutral',
        source: 'N/A',
        detail:
          'Reviews often grouped under Alphabet/Google. DeepMind-specific data is limited.',
      },
    ],
    reviewSnippet:
      'World-class research environment but increasingly feeling the pressure of Alphabet restructuring. Benefits are excellent through Google.',
    reviewRating: 4.3,
    blindBreakdown: {
      overall: 4.3,
      reviewCount: 85,
      careerGrowth: 4.7,
      compensationBenefits: 4.8,
      management: 3.9,
      workLifeBalance: 3.6,
      companyCulture: 4.3,
    },
    glassdoorRating: 4.5,
    glassdoorBreakdown: {
      overall: 4.5,
      reviewCount: 110,
      careerGrowth: 4.8,
      compensationBenefits: 4.9,
      management: 4.1,
      workLifeBalance: 3.8,
      companyCulture: 4.5,
    },
    lastUpdated: 'Mar 28, 2026',
  },
  {
    id: '3',
    name: 'Hugging Face',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/wrjWHVCbymPYWzJePqNjBM/huggingface.png',
    industry: 'AI / Open Source',
    size: '100-500',
    stage: 'Series D',
    funding: '$395M',
    matchScore: 96,
    signals: [
      {
        id: 's9',
        text: 'Remote-first',
        type: 'positive',
        source: 'Careers page, employee reviews',
        detail:
          'Fully remote company with optional co-working stipend. No RTO plans.',
      },
      {
        id: 's10',
        text: 'Trans healthcare',
        type: 'positive',
        source: 'Benefits documentation 2025',
        detail:
          'Full coverage for gender-affirming care including surgeries and hormone therapy.',
      },
      {
        id: 's11',
        text: 'ERG programs',
        type: 'positive',
        source: 'Company blog, employee reviews',
        detail:
          '5 active ERGs including LGBTQ+, BIPOC, Women in AI, and Disability Alliance.',
      },
      {
        id: 's12',
        text: 'Supports progressive causes',
        type: 'positive',
        source: 'OpenSecrets, public statements',
        detail:
          'Active advocacy for open-source AI policy and equitable AI access globally.',
      },
    ],
    reviewSnippet:
      "Incredible mission-driven culture. Open source ethos extends to how they treat employees. Best work-life balance I've experienced in tech.",
    reviewRating: 4.9,
    blindBreakdown: {
      overall: 4.9,
      reviewCount: 28,
      careerGrowth: 4.8,
      compensationBenefits: 4.7,
      management: 4.8,
      workLifeBalance: 5.0,
      companyCulture: 4.9,
    },
    glassdoorRating: 4.8,
    glassdoorBreakdown: {
      overall: 4.8,
      reviewCount: 35,
      careerGrowth: 4.7,
      compensationBenefits: 4.6,
      management: 4.7,
      workLifeBalance: 4.9,
      companyCulture: 4.8,
    },
    lastUpdated: 'Feb 10, 2026',
  },
  {
    id: '4',
    name: 'Meta',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/kH5AKXmypV8GBf7qWZa2wh/meta.png',
    industry: 'AI / Social Media',
    size: '50k+',
    stage: 'Public',
    funding: 'NASDAQ: META',
    matchScore: 42,
    signals: [
      {
        id: 's13',
        text: 'Recent layoffs',
        type: 'negative',
        source: 'Layoffs.fyi, SEC filing',
        detail:
          'Multiple rounds of layoffs in 2024-2025, totaling 20,000+ positions eliminated.',
      },
      {
        id: 's14',
        text: 'Anti-DEI donations',
        type: 'negative',
        source: 'OpenSecrets, FEC filings',
        detail:
          'Executive donations and PAC contributions to candidates opposing DEI legislation.',
      },
      {
        id: 's15',
        text: 'Pay equity audit',
        type: 'positive',
        source: 'SEC proxy statement 2025',
        detail:
          'Completed third-party pay equity audit. Published results showing 99% pay parity.',
      },
      {
        id: 's16',
        text: 'Crunch culture reported',
        type: 'negative',
        source: 'Teamblind, Glassdoor (500+ reviews)',
        detail:
          '65% of reviews mention intense pressure and long hours, especially in AI division.',
      },
    ],
    reviewSnippet:
      'Compensation is top of market but the culture has become fear-driven. DEI programs were quietly scaled back after 2024.',
    reviewRating: 3.2,
    blindBreakdown: {
      overall: 3.2,
      reviewCount: 520,
      careerGrowth: 3.5,
      compensationBenefits: 4.6,
      management: 2.8,
      workLifeBalance: 2.4,
      companyCulture: 2.9,
    },
    glassdoorRating: 3.4,
    glassdoorBreakdown: {
      overall: 3.4,
      reviewCount: 680,
      careerGrowth: 3.7,
      compensationBenefits: 4.7,
      management: 3.0,
      workLifeBalance: 2.6,
      companyCulture: 3.1,
    },
    lastUpdated: 'Mar 02, 2026',
  },
  {
    id: '5',
    name: 'Mistral AI',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/bSQvQo1enr9qxiCVHa4Tvq/mistral.png',
    industry: 'AI / LLMs',
    size: '100-500',
    stage: 'Series B',
    funding: '€600M',
    matchScore: 68,
    signals: [
      {
        id: 's17',
        text: 'No layoffs 2yr',
        type: 'neutral',
        source: 'Layoffs.fyi',
        detail:
          'No layoffs reported, but company is only 2 years old with rapid growth.',
      },
      {
        id: 's18',
        text: 'Flexible hours',
        type: 'positive',
        source: 'Employee reviews, careers page',
        detail:
          'Flexible schedule with Paris HQ. Core hours for collaboration, async otherwise.',
      },
      {
        id: 's19',
        text: 'Benefits unknown',
        type: 'neutral',
        source: 'N/A',
        detail:
          'French labor law provides strong baseline. Company-specific benefits not publicly documented.',
      },
      {
        id: 's20',
        text: 'No DEI report',
        type: 'negative',
        source: 'Company website audit',
        detail:
          'No public DEI report or diversity metrics. Common for early-stage EU startups.',
      },
    ],
    reviewSnippet:
      'Exciting frontier AI work with brilliant colleagues. Very fast-paced. Benefits are solid by EU standards but DEI efforts are nascent.',
    reviewRating: 4.1,
    blindBreakdown: {
      overall: 4.1,
      reviewCount: 15,
      careerGrowth: 4.5,
      compensationBenefits: 4.0,
      management: 3.9,
      workLifeBalance: 3.7,
      companyCulture: 4.2,
    },
    glassdoorRating: 4.0,
    glassdoorBreakdown: {
      overall: 4.0,
      reviewCount: 22,
      careerGrowth: 4.3,
      compensationBenefits: 3.9,
      management: 3.8,
      workLifeBalance: 3.6,
      companyCulture: 4.1,
    },
    lastUpdated: 'Mar 30, 2026',
  },
  {
    id: '6',
    name: 'OpenAI',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/3sRpWPzeV7THWk8extXu5a/openai.png',
    industry: 'AI / AGI Research',
    size: '1k-5k',
    stage: 'Private',
    funding: '$13.3B',
    matchScore: 58,
    signals: [
      {
        id: 's21',
        text: 'Recent layoffs',
        type: 'negative',
        source: 'Layoffs.fyi, internal reports',
        detail:
          'Restructured safety and policy teams in 2025, displacing ~120 employees.',
      },
      {
        id: 's22',
        text: 'Trans healthcare',
        type: 'positive',
        source: 'Benefits documentation 2025',
        detail: 'Full coverage for gender-affirming care through Cigna plan.',
      },
      {
        id: 's23',
        text: 'Crunch culture reported',
        type: 'negative',
        source: 'Teamblind, Glassdoor (300+ reviews)',
        detail:
          '70% of reviews mention intense deadlines and 60+ hour weeks, especially around launches.',
      },
      {
        id: 's24',
        text: 'Pay equity audit',
        type: 'positive',
        source: 'Internal memo, leaked 2025',
        detail:
          'Completed pay equity audit with Syndio. Adjusted salaries for 85 employees.',
      },
    ],
    reviewSnippet:
      "You'll work on the most important technology of our time, but at a real personal cost. The pace is unsustainable for most people.",
    reviewRating: 3.6,
    blindBreakdown: {
      overall: 3.6,
      reviewCount: 310,
      careerGrowth: 4.2,
      compensationBenefits: 4.8,
      management: 3.0,
      workLifeBalance: 2.1,
      companyCulture: 3.4,
    },
    glassdoorRating: 3.8,
    glassdoorBreakdown: {
      overall: 3.8,
      reviewCount: 280,
      careerGrowth: 4.3,
      compensationBenefits: 4.9,
      management: 3.2,
      workLifeBalance: 2.3,
      companyCulture: 3.6,
    },
    lastUpdated: 'Mar 25, 2026',
  },
  {
    id: '7',
    name: 'Perplexity',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/2xkMfHvkHofbkCeq3bUmYX/perplexity.png',
    industry: 'AI / Search',
    size: '100-500',
    stage: 'Series B',
    funding: '$500M',
    matchScore: 74,
    signals: [
      {
        id: 's25',
        text: 'No layoffs 2yr',
        type: 'neutral',
        source: 'Layoffs.fyi',
        detail:
          'No layoffs reported. Company has been in hyper-growth mode since 2024.',
      },
      {
        id: 's26',
        text: 'Flexible hours',
        type: 'positive',
        source: 'Employee reviews, careers page',
        detail:
          'Hybrid model with flexible hours. SF HQ with optional remote days.',
      },
      {
        id: 's27',
        text: 'No DEI report',
        type: 'negative',
        source: 'Company website audit',
        detail:
          'No public DEI report or diversity metrics published. Early-stage focus on growth.',
      },
      {
        id: 's28',
        text: 'Partner healthcare',
        type: 'positive',
        source: 'Benefits page 2025',
        detail:
          'Full medical, dental, and vision coverage extended to domestic partners.',
      },
    ],
    reviewSnippet:
      'Fast-moving startup energy with smart people. Benefits are surprisingly good for the stage. DEI is not yet a priority though.',
    reviewRating: 4.2,
    blindBreakdown: {
      overall: 4.2,
      reviewCount: 18,
      careerGrowth: 4.6,
      compensationBenefits: 4.4,
      management: 4.0,
      workLifeBalance: 3.5,
      companyCulture: 4.3,
    },
    glassdoorRating: 4.1,
    glassdoorBreakdown: {
      overall: 4.1,
      reviewCount: 25,
      careerGrowth: 4.5,
      compensationBenefits: 4.3,
      management: 3.9,
      workLifeBalance: 3.4,
      companyCulture: 4.2,
    },
    lastUpdated: 'Mar 18, 2026',
  },
  {
    id: '8',
    name: 'xAI',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/fUEqvJSWiPpGV3jUkADj1U/xai.png',
    industry: 'AI / AGI Research',
    size: '500-1k',
    stage: 'Series C',
    funding: '$12B',
    matchScore: 25,
    signals: [
      {
        id: 's29',
        text: 'Anti-DEI donations',
        type: 'negative',
        source: 'OpenSecrets, FEC filings, public statements',
        detail:
          'Founder and executives have publicly opposed DEI initiatives and donated to anti-DEI causes.',
      },
      {
        id: 's30',
        text: 'No DEI report',
        type: 'negative',
        source: 'Company website audit',
        detail:
          'No DEI report, no diversity metrics, no public commitment to inclusion.',
      },
      {
        id: 's31',
        text: 'Crunch culture reported',
        type: 'negative',
        source: 'Teamblind (50+ posts)',
        detail:
          'Reports of extreme hours, sleeping at office, and burnout culture from day one.',
      },
      {
        id: 's32',
        text: 'Benefits unknown',
        type: 'neutral',
        source: 'N/A',
        detail:
          'Benefits details not publicly available. Company has not responded to surveys.',
      },
    ],
    reviewSnippet:
      'Cutting-edge work but the culture is brutal. No work-life balance whatsoever. If you care about DEI, look elsewhere.',
    reviewRating: 2.4,
    blindBreakdown: {
      overall: 2.4,
      reviewCount: 55,
      careerGrowth: 3.2,
      compensationBenefits: 4.5,
      management: 1.8,
      workLifeBalance: 1.5,
      companyCulture: 2.0,
    },
    glassdoorRating: 2.6,
    glassdoorBreakdown: {
      overall: 2.6,
      reviewCount: 40,
      careerGrowth: 3.4,
      compensationBenefits: 4.6,
      management: 2.0,
      workLifeBalance: 1.7,
      companyCulture: 2.2,
    },
    lastUpdated: 'Mar 29, 2026',
  },
]
